import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PaymentHistory.css";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH STORAGE KEYS
============================== */
const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "email",
  "userEmail",
  "user_email",
  "currentUser",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  const tokenKeys = ["token", "authToken", "accessToken", "userToken", "jwtToken"];

  for (const key of tokenKeys) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return {};

  try {
    const parsed = JSON.parse(rawUser);

    if (!parsed || typeof parsed !== "object") return {};

    return parsed;
  } catch {
    return {};
  }
};

/* ==============================
   DISPLAY HELPERS
============================== */
const formatAmount = (value) => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return "₹0";

  if (cleanValue.startsWith("₹")) return cleanValue;

  return `₹${cleanValue}`;
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
};

const maskPaymentId = (value = "") => {
  const paymentId = String(value || "").trim();

  if (!paymentId) return "-";

  if (paymentId.length <= 8) return paymentId;

  return `${paymentId.slice(0, 6)}...${paymentId.slice(-6)}`;
};

const getStatusLabel = (status = "") => {
  const clean = String(status || "").trim().toLowerCase();

  if (clean === "success") return "Success";
  if (clean === "pending") return "Pending";
  if (clean === "failed") return "Failed";

  return clean || "Unknown";
};

const defaultStats = {
  total: 0,
  successful: 0,
  pending: 0,
  failed: 0,
};

const PaymentHistory = () => {
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const user = useMemo(() => getStoredUser(), []);

  const fullName =
    user?.fullName ||
    user?.name ||
    user?.realName ||
    user?.username ||
    "User";

  /* ==============================
     PROTECTED API FETCH
  ============================== */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  /* ==============================
     LOAD PAYMENT HISTORY
  ============================== */
  const fetchPaymentHistory = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);

        setStatus({ type: "", message: "" });

        const data = await authFetch("/payments/history");

        const paymentList = Array.isArray(data?.payments)
          ? data.payments
          : Array.isArray(data?.data)
          ? data.data
          : [];

        setPayments(paymentList);

        setStats({
          total: Number(data?.stats?.total ?? paymentList.length ?? 0),
          successful: Number(
            data?.stats?.successful ??
              paymentList.filter((item) => item.status === "success").length
          ),
          pending: Number(
            data?.stats?.pending ??
              paymentList.filter((item) => item.status === "pending").length
          ),
          failed: Number(
            data?.stats?.failed ??
              paymentList.filter((item) => item.status === "failed").length
          ),
        });
      } catch (error) {
        setPayments([]);
        setStats(defaultStats);

        setStatus({
          type: "error",
          message: error.message || "Failed to fetch payment history.",
        });

        if (
          String(error.message || "")
            .toLowerCase()
            .includes("session expired")
        ) {
          setTimeout(() => {
            navigate("/signin");
          }, 800);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [authFetch, navigate]
  );

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchPaymentHistory(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment updated successfully.",
        });

        softRefresh();
      }
    };

    const handleDashboardUpdate = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleNotification = () => {
      softRefresh();
    };

    const handleActivity = () => {
      softRefresh();
    };

    const handleSocketAuthError = () => {
      clearAuth();
      navigate("/signin");
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);
    socket.on("payment:status", handlePaymentStatus);
    socket.on("dashboard:update", handleDashboardUpdate);
    socket.on("notification:new", handleNotification);
    socket.on("activity:new", handleActivity);
    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);
      socket.off("payment:status", handlePaymentStatus);
      socket.off("dashboard:update", handleDashboardUpdate);
      socket.off("notification:new", handleNotification);
      socket.off("activity:new", handleActivity);
      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchPaymentHistory, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    fetchPaymentHistory();
  };

  return (
    <section className="payhis-page">
      <div className="payhis-container">
        <div className="payhis-topbar">
          <button
            type="button"
            className="payhis-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>

          <button
            type="button"
            className="payhis-back-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="payhis-header">
          <span className="payhis-badge">Payment History</span>

          <h1>{fullName}&apos;s Payments</h1>

          <p>
            View all completed, pending, or failed payments linked securely to
            your account.
          </p>

          <div className="payhis-realtime-chip">
            <span
              className={
                realtimeStatus === "connected"
                  ? "payhis-realtime-dot online"
                  : "payhis-realtime-dot offline"
              }
            />

            <span>
              {realtimeStatus === "connected"
                ? "Realtime connected"
                : realtimeStatus === "connecting"
                ? "Connecting realtime..."
                : "Realtime offline"}
            </span>
          </div>
        </div>

        <div className="payhis-summary-grid">
          <div className="payhis-summary-card">
            <span>Total Payments</span>
            <strong>{stats.total}</strong>
          </div>

          <div className="payhis-summary-card">
            <span>Successful</span>
            <strong>{stats.successful}</strong>
          </div>

          <div className="payhis-summary-card">
            <span>Pending</span>
            <strong>{stats.pending}</strong>
          </div>

          <div className="payhis-summary-card">
            <span>Failed</span>
            <strong>{stats.failed}</strong>
          </div>
        </div>

        {status.message ? (
          <div className={`payhis-alert ${status.type}`}>{status.message}</div>
        ) : null}

        <div className="payhis-card">
          <div className="payhis-card-head">
            <h3>Recent Transactions</h3>
          </div>

          {loading ? (
            <div className="payhis-empty-state">Loading payment history...</div>
          ) : payments.length === 0 ? (
            <div className="payhis-empty-state">
              No payment history found for this account.
            </div>
          ) : (
            <div className="payhis-table-wrap">
              <table className="payhis-table">
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Product / Plan</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>
                  {payments.map((item) => (
                    <tr key={item.id || item.paymentId}>
                      <td title={item.paymentId || ""}>
                        {maskPaymentId(item.paymentId || `PAY-${item.id}`)}
                      </td>

                      <td>{item.planName || "-"}</td>

                      <td>{formatAmount(item.amount || item.rawAmount)}</td>

                      <td>{item.paymentMethod || "razorpay"}</td>

                      <td>
                        <span
                          className={`payhis-status ${String(
                            item.status || ""
                          ).toLowerCase()}`}
                        >
                          {getStatusLabel(item.status)}
                        </span>
                      </td>

                      <td>
                        {item.createdAtFormatted ||
                          formatDate(item.createdAt) ||
                          "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PaymentHistory;